import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Terminal } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { SkeletonCard } from '../components/ui/Skeleton';
import { KqlEditor } from '../components/investigation';
import { getIncidentDetail, getIncidentAlerts } from '../services';
import { getTenantCapabilities, type TenantCapabilitiesResponse } from '../services/investigation';
import type { Entity, HuntingCapability } from '../data/types';

interface EntitySnippet {
  type: string;
  name: string;
  value: string;
}

function toSnippets(entities: Entity[]): EntitySnippet[] {
  const snippets: EntitySnippet[] = [];
  for (const e of entities) {
    const value = e.value
      || e.details?.userPrincipalName
      || e.details?.upn
      || e.details?.deviceId
      || e.details?.id
      || e.name;
    if (!value) continue;
    if (e.type === 'user') {
      snippets.push({ type: 'user', name: e.name, value });
      if (value.includes('@')) {
        snippets.push({ type: 'email', name: e.name, value });
      }
    } else if (e.type === 'device') {
      snippets.push({ type: 'device', name: e.name, value });
    } else if (e.type === 'ip') {
      snippets.push({ type: 'ip', name: e.name, value });
    }
  }
  return snippets;
}

export function IncidentKqlQuery() {
  const { tenantId, incidentId } = useParams<{ tenantId: string; incidentId: string }>();
  const navigate = useNavigate();

  const [entities, setEntities] = useState<Entity[]>([]);
  const [alertIds, setAlertIds] = useState<string[]>([]);
  const [tenantDisplayName, setTenantDisplayName] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [capabilities, setCapabilities] =
    useState<TenantCapabilitiesResponse['capabilities']>(null);
  const [capabilitiesRefreshedAt, setCapabilitiesRefreshedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchContext() {
      if (!tenantId || !incidentId) return;
      setIsLoading(true);
      // Capabilities fetch runs in parallel with the incident context — it
      // never throws and falls back to null on error, so we don't block
      // the editor on it. The CapabilityStrip will simply not render until
      // it lands; the empty-state hint logic fails open (no hints).
      const [detail, alerts, caps] = await Promise.all([
        getIncidentDetail(tenantId, incidentId),
        getIncidentAlerts(tenantId, incidentId),
        getTenantCapabilities(tenantId),
      ]);
      if (cancelled) return;
      if (detail.success && detail.data) {
        setEntities(detail.data.entities);
        setTenantDisplayName(detail.data.incident.tenantDisplayName);
      }
      if (alerts.success && alerts.data) {
        setAlertIds(alerts.data.map((a) => a.alertId));
      }
      if (caps.success && caps.data) {
        setCapabilities(caps.data.capabilities);
        setCapabilitiesRefreshedAt(caps.data.lastRefreshedAt);
      }
      setIsLoading(false);
    }
    fetchContext();
    return () => {
      cancelled = true;
    };
  }, [tenantId, incidentId]);

  const entitySnippets = useMemo(() => toSnippets(entities), [entities]);

  if (!tenantId || !incidentId) {
    return (
      <div className="p-6">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-xl font-semibold text-text-primary mb-2">Invalid Parameters</h2>
          <p className="text-text-secondary mb-4">Missing tenant or incident information.</p>
          <Button onClick={() => navigate('/incidents')}>
            <ArrowLeft className="w-4 h-4" />
            Back to Incidents
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none p-6 space-y-4 animate-fade-in">
      <button
        onClick={() => navigate(`/incidents/${tenantId}/${incidentId}`)}
        className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back to Incident #{incidentId}</span>
      </button>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-accent-blue" />
            Custom KQL Query
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-secondary">
            Run arbitrary Advanced Hunting KQL against 30 days of Defender data for this tenant.
            Use the context chips below the editor to insert filters for this incident, its alerts,
            or its entities.
          </p>
          {tenantDisplayName && (
            <p className="text-xs text-text-secondary mt-2">Tenant: {tenantDisplayName}</p>
          )}
        </CardContent>
      </Card>

      {isLoading ? (
        <SkeletonCard />
      ) : (
        <KqlEditor
          tenantId={tenantId}
          incidentId={incidentId}
          alertIds={alertIds}
          entities={entitySnippets}
          capabilities={capabilities as Partial<Record<HuntingCapability, boolean>> | null}
          capabilitiesRefreshedAt={capabilitiesRefreshedAt}
        />
      )}
    </div>
  );
}
